import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { Loading } from '@this/src/components/shared/ui/feedbacks/loading';
import StationIcon from '@this/src/components/shared/icons/station_icon';
import AirportIcon from '@this/src/components/shared/icons/airport_icon';
import { Box } from '../../shared/box_parts';
import SelectTransportBoxRightInOutlineTemplate from '../right/right_in_outline.template';
import SelectTransportBoxRightInListTemplate from '../right/right_in_list.template';
import SelectTransportBoxRightInDomesticAirListTemplate from '../right/right_in_domestic_air_list.template';
import type SelectStore from '../../../../../domain/select_store';
import type Transit from '../../../../../domain/transit/transit';
import { buildDomesticAirTicketLabel, type TransitPriceJson } from '../../../../../domain/select_repository';
import { fetchDomesticAirPrices, fetchActionStatus } from '../../../../../api/domestic_air/prices_api';

// ポーリング設定
const POLLING_INTERVAL_MS = 20000;
const POLLING_TIMEOUT_MS = 300000;

// 自動価格取得の共有フラグ（全DomesticTransportインスタンス間で共有）
// 航路ごとにキー管理し、同じ航路の重複取得を防止
const autoFetchedRoutes = new Set<string>();

interface DomesticTransportProps {
  transport: Transit;
  inList: boolean;
  selected: boolean;
  domesticAirPriceIndex: number;
  loading: boolean;
  handleDomesticAirPriceChange: (index: number) => void;
  handleChange: () => void;
  store: SelectStore;
  isRequestForm: boolean;
  disabled: boolean;
}

const DomesticTransport = ({
  transport,
  inList,
  selected,
  domesticAirPriceIndex,
  loading,
  handleDomesticAirPriceChange,
  handleChange,
  store,
  isRequestForm,
  disabled
}: DomesticTransportProps) => {
  // price_expired のフライトの価格を自動取得するためのローカルステート
  const [prices, setPrices] = useState<TransitPriceJson[] | null>(transport.prices);
  const [isFetchingPrices, setIsFetchingPrices] = useState(false);

  // 航路キー（同じ航路の重複取得防止用・loading共有用）
  // 乗り継ぎ便（Segment 2つ）の場合、全体のfrom/toを使う
  const routeKey = useMemo(() => {
    const airSegments = transport.segments.filter(s => s.isAir());
    if (airSegments.length === 0) return null;
    const firstLeg = airSegments[0].firstLeg();
    const lastLeg = airSegments[airSegments.length - 1].lastLeg();
    if (!firstLeg || !lastLeg) return null;
    const from = firstLeg.from?.code;
    const to = lastLeg.to?.code;
    const date = typeof firstLeg.from?.time_full === 'string' ? firstLeg.from.time_full.split('T')[0] : null;
    return from && to && date ? `${from}-${to}-${date}` : null;
  }, [transport.segments]);

  const departureDate = useMemo(() => {
    const airSegments = transport.segments.filter(s => s.isAir());
    return airSegments[0]?.firstLeg()?.from?.time_full ?? null;
  }, [transport.segments]);

  // store経由で航路全体の取得中フラグを参照（全便でloading共有）
  const isFetchingRoute = routeKey ? store.fetchingPriceRoutes.has(routeKey) : false;

  // transport.prices が変更されたらローカルステートを更新
  useEffect(() => {
    setPrices(transport.prices);
  }, [transport.prices]);

  // 価格更新処理
  const updatePricesFromResponse = useCallback(
    (
      fromAirport: string,
      toAirport: string,
      targetDate: string,
      responsePrices: {
        flightNumber: string;
        changeable: boolean;
        seatType: string;
        price: number;
        ticketType: string;
        seatCount: string | null;
        connectingGroup: string | null;
      }[]
    ) => {
      if (!prices) return;

      const airSegments = transport.segments.filter(s => s.isAir());
      const firstLeg = airSegments[0]?.firstLeg();
      const connectingGroup = prices[0]?.connecting_group;
      const flightNumber =
        prices[0]?.flight_number ||
        (firstLeg ? `${firstLeg.airline_name || ''}${firstLeg.name || ''}`.trim() : undefined) ||
        firstLeg?.name;
      const flightPrices = connectingGroup
        ? responsePrices.filter(rp => rp.connectingGroup === connectingGroup)
        : responsePrices.filter(rp => rp.flightNumber === flightNumber && !rp.connectingGroup);
      if (flightPrices.length > 0) {
        const updatedPrices = flightPrices.map(rp => ({
          flight_number: rp.flightNumber,
          price: rp.price,
          seat_type: rp.seatType,
          ticket_category: rp.changeable ? 'changeable' : 'unchangeable',
          ticket_type: rp.ticketType,
          seat_count: rp.seatCount,
          price_expired: false,
          ...(rp.connectingGroup ? { connecting_group: rp.connectingGroup } : {})
        }));

        transport.prices = updatedPrices as TransitPriceJson[];
        (transport as any).pricesOriginal = updatedPrices as TransitPriceJson[];
        if ((transport as any).raw) {
          (transport as any).raw.prices = updatedPrices;
        }
        setPrices(updatedPrices as TransitPriceJson[]);
      }
      store.updateAllTransitPrices(fromAirport, toAirport, targetDate, responsePrices);
    },
    [prices, store, transport]
  );

  // 価格取得処理（フロントエンドポーリング方式）
  const fetchPricesCore = useCallback(async () => {
    if (!prices || !transport.airExists() || isFetchingPrices) {
      return;
    }

    // 航空セグメントを取得（乗り継ぎ便の場合、全体のfrom/toを使う）
    const airSegments = transport.segments.filter(s => s.isAir());
    if (airSegments.length === 0) {
      return;
    }

    const sources = Array.from(new Set(airSegments.flatMap(s => s.sources ?? [])));
    if (sources.length === 0) {
      return;
    }

    // 空港コードと日付を取得（乗り継ぎ便: 最初のSegmentのfromと最後のSegmentのtoを使う）
    const firstLeg = airSegments[0].firstLeg();
    const lastLeg = airSegments[airSegments.length - 1].lastLeg();
    const fromAirport = firstLeg?.from?.code;
    const toAirport = lastLeg?.to?.code;
    const targetDateStr = firstLeg?.from?.time_full;
    if (!fromAirport || !toAirport || !targetDateStr) {
      return;
    }

    const targetDate =
      typeof targetDateStr === 'string'
        ? targetDateStr.split('T')[0]
        : new Date(targetDateStr).toISOString().split('T')[0];

    setIsFetchingPrices(true);
    // store経由で航路全体のloading状態を共有
    if (routeKey) {
      store.fetchingPriceRoutes.add(routeKey);
      app.render();
    }

    // 同じ航路の全transit IDを収集（DB更新用）
    const transitIds = store.collectTransitIds(fromAirport, toAirport, targetDate);

    try {
      // Step 1: アクション作成APIを呼ぶ
      const response = await fetchDomesticAirPrices({
        from_airport: fromAirport,
        to_airport: toAirport,
        target_date: targetDate,
        sources,
        search_query_id: store.result.searchQueryId,
        transit_ids: transitIds
      });

      // Step 2: actionIdsがあればポーリング開始
      if (response.actionIds && response.actionIds.length > 0) {
        const startTime = Date.now();

        // ポーリングループ
        const pollStatus = async (): Promise<boolean> => {
          // タイムアウトチェック
          if (Date.now() - startTime > POLLING_TIMEOUT_MS) {
            console.warn('fetchPricesCore: polling timeout');
            return false;
          }

          try {
            const statusResponse = await fetchActionStatus(response.actionIds!);
            if (statusResponse.allCompleted) {
              return true;
            }

            // まだ完了していない場合は待機して再ポーリング
            await new Promise<void>(resolve => {
              setTimeout(resolve, POLLING_INTERVAL_MS);
            });
            return await pollStatus();
          } catch (error) {
            console.error('fetchPricesCore: polling error', error);
            return false;
          }
        };

        const completed = await pollStatus();
        if (completed) {
          // Step 3: 完了したら価格を再取得（アクション作成しない）
          const updatedResponse = await fetchDomesticAirPrices({
            from_airport: fromAirport,
            to_airport: toAirport,
            target_date: targetDate,
            sources,
            prices_only: true,
            search_query_id: store.result.searchQueryId,
            transit_ids: transitIds
          });

          if (updatedResponse.prices.length > 0) {
            updatePricesFromResponse(fromAirport, toAirport, targetDate, updatedResponse.prices);
          }
        }
      } else if (response.success && response.prices.length > 0) {
        // actionIdsがない場合（キャッシュから取得できた場合など）
        updatePricesFromResponse(fromAirport, toAirport, targetDate, response.prices);
      }
    } catch (error) {
      console.error('fetchPricesCore: Failed to fetch prices:', error);
    } finally {
      setIsFetchingPrices(false);
      if (routeKey) {
        store.fetchingPriceRoutes.delete(routeKey);
        app.render();
      }
    }
  }, [prices, transport, isFetchingPrices, updatePricesFromResponse, routeKey, store]);

  // price_expired のフライトを検出して価格を自動取得（検索完了後に実行）
  const isSearching = store.result.items.some(item => item.loading);
  useEffect(() => {
    if (!routeKey) {
      return;
    }
    if (autoFetchedRoutes.has(routeKey)) {
      return;
    }
    if (isSearching) {
      return;
    }
    if (!prices) {
      return;
    }
    const hasExpiredPrices = prices.some(p => p.price_expired);
    if (!hasExpiredPrices) {
      return;
    }

    autoFetchedRoutes.add(routeKey);
    fetchPricesCore();
  }, [prices, fetchPricesCore, isSearching, routeKey]);

  const priceData = useMemo(() => {
    return prices?.[domesticAirPriceIndex] ?? transport.pricesOriginal?.[domesticAirPriceIndex];
  }, [prices, transport.pricesOriginal, domesticAirPriceIndex]);
  const styles = useMemo(() => getStyles(), []);

  // 航空セグメントのsourcesを取得（ANAキャッシュ由来かどうかの判定に使用）
  const airSegmentSources = useMemo(() => {
    const airSegs = transport.segments.filter(s => s.isAir());
    return Array.from(new Set(airSegs.flatMap(s => s.sources ?? [])));
  }, [transport.segments]);

  const renderRightContent = () => {
    if (inList === false) {
      return (
        <SelectTransportBoxRightInOutlineTemplate
          price={((): number | undefined => {
            if (transport.package) {
              return transport.additionalPrice || 0;
            }
            if (priceData) {
              return priceData.price ?? undefined;
            }
            return transport.price ?? undefined;
          })()}
          isAdditional={transport.package === true}
          priceRange={store.result.type === 'airPackage' ? transport.price_range ?? undefined : undefined}
          includeTax={transport.includeTax}
          selected={selected}
          onClick={handleChange}
          showsPricePerPersonText={store.result.type !== 'airPackage'}
          showsIncludeTax={store.result.type !== 'airPackage'}
          isRequestForm={isRequestForm || false}
          isRakutenPackage={transport.isRakutenPackage}
          disabled={store.result.type === 'airPackage' && store.isCurrentAirlinePackageLoading}
        />
      );
    }

    if (transport.package) {
      return (
        <SelectTransportBoxRightInListTemplate
          loading={loading}
          selected={selected}
          hovered={transport.hovered}
          priceRange={store.result.type === 'airPackage' ? transport.price_range ?? undefined : undefined}
          showsPricePerPersonText={store.result.type !== 'airPackage'}
          onClick={handleChange}
          onTouchStart={transport.handleTouched}
          onMouseOver={() => transport.setHovered(true)}
          onMouseLeave={() => transport.setHovered(false)}
          isPackage={transport.package}
          isDomestic={store.result.isDomesticAirResult()}
          disabled={disabled}
        />
      );
    }

    if (transport.airExists()) {
      return (
        <div style={{ ...styles.separateFlightSelect, padding: '5px 5px 0px 0px' }} className="flight">
          {prices &&
            prices.map((p, i) => (
              <div key={i} onClick={() => handleDomesticAirPriceChange(i)}>
                <SelectTransportBoxRightInDomesticAirListTemplate
                  key={i}
                  loading={loading}
                  selected={selected && priceData != null && p === priceData}
                  price={p}
                  departureDate={departureDate}
                  flightNumber={transport.firstLegName()}
                  sources={airSegmentSources}
                  fetchingExpiredPrice={(isFetchingPrices || isFetchingRoute) && p.price_expired === true}
                />
              </div>
            ))}
        </div>
      );
    }

    return (
      <SelectTransportBoxRightInListTemplate
        loading={loading}
        selected={selected}
        hovered={transport.hovered}
        price={transport.price ?? undefined}
        isDomestic={store.result.isDomesticAirResult()}
        onClick={handleChange}
        onTouchStart={transport.handleTouched}
        onMouseOver={() => transport.setHovered(true)}
        onMouseLeave={() => transport.setHovered(false)}
      />
    );
  };

  return (
    <Box className="domestic-transport-box" selected={selected}>
      <div style={styles.left}>
        {transport.segments.map(s =>
          s.legs.map((l, j) => (
            <div
              key={`${s.firstLegName}_${l.name}`}
              style={styles.segment}
              className="domestic-transport__segment"
            >
              <div style={styles.boxContent}>
                <div style={styles.legTime}>{l.from.time}</div>
                <div style={styles.boxTitle}>出発</div>
                <div style={styles.space} data-wovn-ignore>
                  <div style={styles.name}>{l.from.name === '' ? <Loading size="small" /> : l.from.name}</div>
                </div>
              </div>
              <div style={styles.boxContent}>
                <div style={styles.boxContent}>
                  <div style={styles.legTimeSpace} className="domestic-transport__leg-time-space" />
                  {s.type === 'shin' && <StationIcon />}
                  {s.type === 'air' && <AirportIcon />}
                </div>
                <div style={styles.spaceLeft}>
                  <span>
                    {l.airline_name && `${l.airline_name} `}
                    {l.name}
                    <img style={styles.image} src={s.imagePath()} alt="" />
                  </span>
                  {l.code_share && <div>{`（${l.code_share}）`}</div>}
                </div>
              </div>
              <div style={styles.boxContent}>
                <div style={styles.legTime}>{l.to.time}</div>
                <div style={styles.boxTitle}>到着</div>
                <div style={styles.space} data-wovn-ignore>
                  <div style={styles.name}>{l.to.name === '' ? <Loading size="small" /> : l.to.name}</div>
                </div>
              </div>
              {inList === false && !transport.package && transport.airExists() && priceData && (
                <div style={styles.boxContent}>
                  <div style={styles.legTime} />
                  <div style={styles.boxTitle}>備考</div>
                  <div style={styles.space}>
                    {buildDomesticAirTicketLabel(priceData, departureDate, l.name).ticketLabel}
                    &nbsp;&nbsp;&nbsp;
                    {priceData?.seat_type}
                    &nbsp;&nbsp;&nbsp;
                    {priceData?.seat_count &&
                      priceData.seat_count !== 'unknown' &&
                      (airSegmentSources.length > 0
                        ? String(priceData.seat_count)
                        : priceData.seat_count === '○' || Number(priceData.seat_count) >= 10
                        ? '10席以上'
                        : `${priceData.seat_count}席`)}
                  </div>
                </div>
              )}
              <div data-wovn-ignore className="select-transport-box__left__boarding-arrival">
                {s.shouldShowBoardingStation() && j === 0 && [`乗車駅：${s.boardingName()}`, <br key={0} />]}
                {s.shouldShowArrivalStation() && j === s.legs.length - 1 && `降車駅：${s.arrivalName()}`}
              </div>
            </div>
          ))
        )}
      </div>

      <div className={`select-transport-box__right-wrapper domestic ${inList ? '' : 'detail'}`}>
        {renderRightContent()}
      </div>
    </Box>
  );
};

const getStyles = () => ({
  left: {
    flexShrink: 10,
    padding: '2px 5px 2px 10px',
    margin: 'auto 0'
  } as const,
  segment: {
    // パディングは擬似セレクタが必要なためSCSSファイルで実装
  } as const,
  boxContent: {
    display: 'flex',
    alignItems: 'center',
    height: '2rem'
  } as const,
  name: {
    fontSize: '12px',
    fontWeight: 'bold'
  } as const,
  image: {
    height: '20px',
    marginLeft: '5px',
    verticalAlign: 'middle'
  } as const,
  boxTitle: {
    background: '#aaa',
    color: 'white',
    minWidth: '25px',
    marginRight: '10px',
    height: '16px',
    lineHeight: '16px',
    fontSize: '10px',
    fontWeight: 'bold',
    textAlign: 'center'
  } as const,
  space: {
    marginRight: '10px'
  } as const,
  spaceLeft: {
    marginLeft: '20px'
  } as const,
  legTime: {
    minWidth: '50px',
    color: '#3675ff',
    fontSize: '13px',
    height: '16px',
    fontWeight: 700,
    lineHeight: '16px',
    letterSpacing: '0em',
    textAlign: 'left'
  } as const,
  legTimeSpace: {
    minWidth: '50px',
    color: '#3675ff',
    fontSize: '13px',
    height: '16px',
    fontWeight: 700,
    lineHeight: '16px',
    letterSpacing: '0em',
    textAlign: 'left'
  } as const,
  separateFlightSelect: {
    width: '100%'
  } as const
});

export default DomesticTransport;
